import { isPos } from 'src/config'
import { DELIVERY_LOCATION_TYPES } from 'src/models/delivery-location/DeliveryLocation'
import { DELIVERY_TYPE_OPTIONS } from 'src/models/delivery-type/DeliveryType'

/**
 * Computer that computes available Delivery Type, Location, and Position options for a given Order.
 * Location and Position options will include potential options for Theater Seat Delivery if an
 * inapplicable Delivery Type is selected, to determine whether Theater Seat is a valid Delivery Type.
 */
class OrderDeliveryOptionsComputer {
  constructor () {
    if (!OrderDeliveryOptionsComputer.#instance) {
      OrderDeliveryOptionsComputer.#instance = this
    }
    return OrderDeliveryOptionsComputer.#instance
  }

  compute (order) {
    const deliveryLocationType = this.#deliveryLocationType(order)
    const deliveryLocationOptions = this.#deliveryLocationOptions(order, deliveryLocationType)
    const deliveryPositionOptions = this.#deliveryPositionOptions(order, deliveryLocationOptions, deliveryLocationType)
    const deliveryTypeOptions = this.#deliveryTypeOptions(order, deliveryPositionOptions, deliveryLocationType)

    return {
      deliveryTypeOptions,
      deliveryLocationType,
      deliveryLocationOptions: _.sortBy(deliveryLocationOptions, 'displayOrder'),
      deliveryPositionOptions
    }
  }

  // private
  static #instance = null

  #deliveryLocationType (order) {
    if (_.includes(DELIVERY_TYPE_OPTIONS.DELIVERY_LOCATION_REQUIRED, order.deliveryType)) {
      return order.deliveryType
    } else if (!isPos && order.configuration.currentSite.theaterSeatDeliveryAvailableOnline) {
      return DELIVERY_LOCATION_TYPES.THEATER_SEAT
    } else {
      return null
    }
  }

  #deliveryLocationOptions (order, deliveryType = null) {
    if (_.isEmpty(order.configuration?.deliveryLocations?.data)) return []

    deliveryType ||= this.#deliveryLocationType(order)
    if (_.isNil(deliveryType) || !_.includes(DELIVERY_TYPE_OPTIONS.DELIVERY_LOCATION_REQUIRED, deliveryType)) return []

    const allDeliveryLocationOptions = _.filter(order.configuration.deliveryLocations.data, { type: deliveryType })

    if (deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT) {
      // POS can choose locations even if no showing is present
      if (isPos && _.isEmpty(order.futureShowings)) return allDeliveryLocationOptions

      return _(order.futureShowings).map(showing => {
        return _.find(allDeliveryLocationOptions, { screenId: showing.screenId })
      }).compact().value()
    }

    return allDeliveryLocationOptions
  }

  #deliveryPositionOptions (order, deliveryLocationOptions = [], deliveryType = null) {
    if (_.isEmpty(order.configuration?.deliveryLocations?.data)) return []

    const deliveryLocation = (!_.isNil(order.deliveryLocation) && _.find(order.configuration.deliveryLocations.data, { name: order.deliveryLocation })) || _.first(deliveryLocationOptions)

    if (_.isEmpty(deliveryLocation?.positions)) return []

    deliveryType ||= this.#deliveryLocationType(order)
    if (_.isNil(deliveryType)) return []

    const allDeliveryPositionOptions = _.uniq(deliveryLocation.positions)

    if (deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT) {
      const showings = _.filter(order.futureShowings, { screenId: deliveryLocation.screenId })
      const allSelectedSeats = _(showings).map(showing => order.allSelectedSeats(showing)).flatten().value()

      if (isPos && _.isEmpty(allSelectedSeats)) return allDeliveryPositionOptions

      return _.filter(allDeliveryPositionOptions, deliveryPosition => {
        const rawDeliveryPosition = deliveryPosition.replace(deliveryLocation.positionsPrefix, '')
        return _.includes(allSelectedSeats, rawDeliveryPosition)
      })
    }

    return allDeliveryPositionOptions
  }

  #deliveryTypeOptions (order, deliveryPositionOptions = [], deliveryType = null) {
    // ['direct-to-customer', 'register', 'counter-pickup', 'express-pickup', 'theater-seat', 'table']
    const options = []
    const currentSite = order.configuration.currentSite
    const registerType = order?.currentRegisterStation?.registerType
    const customDeliveryTypes = {}

    if (!_.isNil(registerType)) {
      if (currentSite.counterPickupEnabled && registerType.showCounterPickupOption) options.push(DELIVERY_TYPE_OPTIONS.COUNTER_PICKUP)
      if (currentSite.expressPickupEnabled && registerType.showExpressPickupOption) options.push(DELIVERY_TYPE_OPTIONS.EXPRESS_PICKUP)
      if (currentSite.theaterSeatDeliveryEnabled && registerType.showTheaterSeatDeliveryOption) options.push(DELIVERY_TYPE_OPTIONS.THEATER_SEAT)
      if (currentSite.tableDeliveryEnabled && registerType.showTableDeliveryOption) options.push(DELIVERY_TYPE_OPTIONS.TABLE)

      _.each(registerType.deliveryTypes, deliveryTypeId => {
        const customDeliveryType = _.find(order.configuration.deliveryTypes.data, { id: deliveryTypeId })

        if (!_.isNil(customDeliveryType) && (customDeliveryType.enable || customDeliveryType.id === Number.parseInt(order.deliveryType))) {
          customDeliveryTypes[customDeliveryType.id] = customDeliveryType
          options.push(customDeliveryType.id)
        }
      })
    } else {
      if (currentSite.counterPickupEnabled && currentSite.counterPickupAvailableOnline) options.push(DELIVERY_TYPE_OPTIONS.COUNTER_PICKUP)
      if (currentSite.expressPickupEnabled && currentSite.expressPickupAvailableOnline) options.push(DELIVERY_TYPE_OPTIONS.EXPRESS_PICKUP)
      if (currentSite.theaterSeatDeliveryEnabled && currentSite.theaterSeatDeliveryAvailableOnline && (!order.currentShowing || !order.currentShowing?.isSeatDeliveryDisabled)) {
        // Only add THEATER_SEAT as an online option if there is an applicable seat selected
        // Customers can only add unselected seats as their delivery position via QR Code
        const hasTheaterSeatPosition = order.deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT && !_.isEmpty(order.deliveryPositions)
        const hasTheaterSeatOptions = hasTheaterSeatPosition || (deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT && !_.isEmpty(deliveryPositionOptions))
        if (hasTheaterSeatOptions) options.push(DELIVERY_TYPE_OPTIONS.THEATER_SEAT)
      }
      if (currentSite.tableDeliveryEnabled && currentSite.tableDeliveryAvailableOnline) options.push(DELIVERY_TYPE_OPTIONS.TABLE)

      _.each(order.configuration.deliveryTypes.data, customDeliveryType => {
        if ((customDeliveryType.enable && customDeliveryType.availableOnline) || customDeliveryType.id === Number.parseInt(order.deliveryType)) {
          customDeliveryTypes[customDeliveryType.id] = customDeliveryType
          options.push(customDeliveryType.id)
        }
      })
    }

    const deliveryTypePriority = !_.isNil(registerType) ? registerType.deliveryTypePriority : currentSite.deliveryTypePriorityForRemoteOrders
    // Add all available options that exist in deliveryTypePriority by their order in deliveryTypePriority with intersection
    // Add all available options missing from deliveryTypePriority to end of options by any order with concat difference
    const prioritizedDeliveryTypeOptions = _.concat(_.intersection(deliveryTypePriority, options), _.difference(options, deliveryTypePriority))

    return _.map(prioritizedDeliveryTypeOptions, option => (
      customDeliveryTypes[option] || { label: _.startCase(option), value: option }
    ))
  }
}

const orderDeliveryOptionsComputer = new OrderDeliveryOptionsComputer()
export default orderDeliveryOptionsComputer
